import type {
    ILabelModel,
    ILabelModelPaginationResult,
    IPagination,
    ITagModelObject,
    IValueModelPaginatedResult,
    PermissionType,
} from '@experiences/interfaces';
import { getResourceCatalogRoute } from '@experiences/util';

import {
    axiosDelete,
    get,
    post,
    put,
} from '../utility/Requests';
import {
    cleanLabelTagResponse,
    cleanValueTagResponse,
    DEFAULT_SEARCH_FILTERS,
    mapObjectId,
    mapPagination,
} from './TagsUtil';

export interface ITagLabelDto extends Partial<ITagModelObject>{}

export const tagsUrl = '/api/orchestrator/tags';

export async function getPermissions({
    accountLogicalName, tenantName,
}: {
    url: string;
    accountLogicalName: string;
    tenantName: string | undefined;
    selectedAccountId: string;
}) {
    const url = `${getResourceCatalogRoute(accountLogicalName, tenantName ?? '')}/Entities/Tags/Permissions`;
    const res = await get<PermissionType[]>(url, { ignoreBasePath: true });
    return res.map(permission => permission.toLowerCase()) as PermissionType[];
}

export async function getLabels({
    pagination, accountLogicalName, tenantName,
}: {
    url: string;
    pagination: IPagination;
    accountLogicalName: string;
    tenantName: string | undefined;
    selectedAccountId: string;
}) {
    const url = `${getResourceCatalogRoute(accountLogicalName, tenantName ?? '')}/Tags`;
    const response = await get<ILabelModelPaginationResult>(url, {
        ignoreBasePath: true,
        urlParams: {
            ...DEFAULT_SEARCH_FILTERS,
            ...mapPagination(pagination),
            type: 'Label',
        },
    });

    return cleanLabelTagResponse(response);
}

export async function getKeyValues({
    pagination, accountLogicalName, tenantName,
}: {
    url: string;
    pagination: IPagination;
    accountLogicalName: string;
    tenantName: string | undefined;
    selectedAccountId: string;
}) {
    const url = `${getResourceCatalogRoute(accountLogicalName, tenantName ?? '')}/Tags`;
    const response = await get<ILabelModelPaginationResult>(url, {
        ignoreBasePath: true,
        urlParams: {
            ...DEFAULT_SEARCH_FILTERS,
            ...mapPagination(pagination),
            type: 'KeyValue',
        },
    });

    return cleanLabelTagResponse(response);
}

export async function getLabel({
    accountLogicalName, tenantName, id,
}: {
    url: string;
    accountLogicalName: string;
    tenantName: string | undefined;
    id: string;
    selectedAccountId: string;
}) {
    const url = `${getResourceCatalogRoute(accountLogicalName, tenantName ?? '')}/Tags/${id}`;
    const response = await get<ILabelModel>(url, { ignoreBasePath: true });
    return mapObjectId(response);
}

export async function createLabel(
    accountLogicalName: string,
    tenantName: string | undefined,
    payload: ITagLabelDto,
    _selectedAccountId: string,
) {
    const url = `${getResourceCatalogRoute(accountLogicalName, tenantName ?? '')}/Tags`;
    payload.type = 'Label';
    return post(url, {
        body: payload,
        ignoreBasePath: true,
    });
}

export async function createKeyValue(
    accountLogicalName: string,
    tenantName: string | undefined,
    payload: ITagLabelDto,
    _selectedAccountId: string,
) {
    const url = `${getResourceCatalogRoute(accountLogicalName, tenantName ?? '')}/Tags`;
    payload.type = 'KeyValue';
    return post(url, {
        body: payload,
        ignoreBasePath: true,
    });
}

export async function updateLabel(
    accountLogicalName: string,
    tenantName: string | undefined,
    id: string | undefined,
    payload: ITagLabelDto,
    _selectedAccountId: string,
) {
    const url = `${getResourceCatalogRoute(accountLogicalName, tenantName ?? '')}/Tags/${id}`;
    return put(url, {
        body: payload,
        ignoreBasePath: true,
    });
}

export async function deleteLabel(accountLogicalName: string, tenantName: string | undefined, keys: string[], _selectedAccountId: string) {
    const url = `${getResourceCatalogRoute(accountLogicalName, tenantName ?? '')}/Tags`;
    return axiosDelete(url, {
        ignoreBasePath: true,
        urlParams: {
            keys,
            type: 'Label',
        },
    });
}

export async function deleteKeyValue(
    accountLogicalName: string,
    tenantName: string | undefined,
    keys: string[],
    _selectedAccountId: string,
) {
    const url = `${getResourceCatalogRoute(accountLogicalName, tenantName ?? '')}/Tags`;
    return axiosDelete(url, {
        ignoreBasePath: true,
        urlParams: {
            keys,
            type: 'KeyValue',
        },
    });
}

export async function getValuesForKey({
    pagination, accountLogicalName, tenantName, key,
}: {
    url: string;
    pagination: IPagination;
    accountLogicalName: string;
    tenantName: string;
    key: string;
    selectedAccountId: string;
}) {
    const url = `${getResourceCatalogRoute(accountLogicalName, tenantName)}/Tags/${key}/Values`;
    const response = await get<IValueModelPaginatedResult>(url, {
        ignoreBasePath: true,
        urlParams: {
            ...DEFAULT_SEARCH_FILTERS,
            ...mapPagination(pagination),
        },
    });
    return cleanValueTagResponse(response);
}

export async function createValuesForKey(
    accountLogicalName: string,
    tenantName: string | undefined,
    key: string,
    payload: { labelKey: string; values: string[]; accountKey: string; tenantKey: string },
    _selectedAccountId: string,
) {
    const url = `${getResourceCatalogRoute(accountLogicalName, tenantName ?? '')}/Tags/${key}/Values`;
    return post(url, {
        body: payload,
        ignoreBasePath: true,
    });
}

export async function deleteValueFromKey(
    accountLogicalName: string,
    tenantName: string | undefined,
    key: string,
    valueKey: string,
    _selectedAccountId: string,
) {
    const url = `${getResourceCatalogRoute(accountLogicalName, tenantName ?? '')}/Tags/${key}/Values/${valueKey}`;
    return axiosDelete(url, { ignoreBasePath: true });
}

export async function deleteValuesFromKey(
    accountLogicalName: string,
    tenantName: string | undefined,
    key: string,
    valueKeys: string[],
    _selectedAccountId: string,
) {
    const url = `${getResourceCatalogRoute(accountLogicalName, tenantName ?? '')}/Tags/${key}/Values`;
    return axiosDelete(url, {
        ignoreBasePath: true,
        urlParams: { valueKeys },
    });
}
